<template>
  <ResponsiveTable
    v-bind="{ ...$props, ...$attrs }"
    ref="theTable"
    :columns="augmentedCols"
    :table-title="tableTitle"
    :table-subtitle="tableSubtitle"
    v-on="$listeners"
  >
    <!-- Add our slots we're augmenting here -->
    <template #location="{ value }">
      <span v-tooltip="locName(value)">
        {{ locNameLong(value) }}
      </span>
    </template>
    <template #cloud="{ row, column, value }">
      <span v-tooltip="value" :style="{ color: getCloud(row.agent, column.id)?.color }">
        {{ getCloud(row.agent, column.id)?.name || '' }}
      </span>
    </template>
    <template #continent="{ row, column }">
      <span v-tooltip="getContinent(row.agent, column.id)">
        {{ getContinent(row.agent, column.id) }}
      </span>
    </template>
    <template #country="{ row, column, value }">
      <span v-tooltip="value + ': ' + getCountry(row.agent, column.id)">
        {{ getCountry(row.agent, column.id) }}
      </span>
    </template>
    <template #target_country="{ row, column, value }">
      <span v-tooltip="value + ': ' + getCountry(row.target, column.id)">
        {{ getCountry(row.target, column.id) }}
      </span>
    </template>
    <template #target_continent="{ row, column }">
      <span v-tooltip="getContinent(row.target, column.id)">
        {{ getContinent(row.target, column.id) }}
      </span>
    </template>
    <template #call="{ row }">
      <router-link
        v-tooltip="valueFor(row, 'name')"
        :to="{
          name: isProjectEditor ? 'calls-edit' : 'calls-preview',
          params: { callId: row.id },
        }"
      >
        {{ valueFor(row, 'name') }}
      </router-link>
    </template>
    <template #project="{ row }">
      <span @click.stop>
        <a
          aria-label="Project"
          @click.prevent="goToProject(row.id)"
          @keyup.enter="goToProject(row.id)"
        >
          <i class="glyphicon glyphicon-home" /> {{ row.name }}
        </a>
      </span>
    </template>
    <template #slos="slots">
      <SLOItem v-bind="slots" />
    </template>
    <template #apdex="{ row, column, value }">
      <span
        v-if="value !== null"
        v-tooltip="apdexTooltip(row, column)"
        :class="{
          'color-pass': value >= 0.995,
          'color-warn': value >= 0.505 && value < 0.995,
          'color-fail': value < 0.505,
        }"
      >
        {{ roundFlt(row, column.id, 2) }}
      </span>
      <template v-else> - </template>
    </template>
    <template #apdexDiff="{ row, column, value }">
      <span v-if="value !== null" v-tooltip="roundFlt(row, column.id, 3, true)">
        {{ roundFlt(row, column.id, 2, true) }}
      </span>
      <template v-else> - </template>
    </template>
    <template #casc="{ row, column, value }">
      <span
        v-if="!valueFor(row, column.id + 'Route') && value !== null"
        v-tooltip="cascTooltip(row, column)"
        :class="
          column.baseline
            ? `color-${baselineResult(value, column.baseline, column.type, column.improvement)}`
            : {
                'color-pass': value >= 800,
                'color-warn': value >= 600 && value < 800,
                'color-fail': value < 600,
              }
        "
      >
        {{ roundCasc(row, column.id) }}
      </span>
      <span v-else-if="value !== null" @click.stop>
        <router-link
          v-tooltip="cascTooltip(row, column)"
          :to="valueFor(row, column.id + 'Route')"
          :class="
            column.baseline
              ? `color-${baselineResult(value, column.baseline, column.type, column.improvement)}`
              : {
                  'color-pass': value >= 800,
                  'color-warn': value >= 600 && value < 800,
                  'color-fail': value < 600,
                }
          "
        >
          {{ roundCasc(row, column.id) }}
        </router-link>
      </span>
      <template v-else> - </template>
    </template>
    <template #cascDiff="{ row, column, value }">
      <span
        v-if="value !== null"
        v-tooltip="roundCasc(row, column.id, true) + ' change'"
        :class="
          column.baseline
            ? `color-${baselineResult(value, column.baseline, column.type, column.improvement)}`
            : {
                'color-pass': value > (column.threshold || 0),
                'color-fail': value < (-column.threshold || 0),
              }
        "
      >
        {{ roundCasc(row, column.id, true) }}
      </span>
      <template v-else> - </template>
    </template>
    <template #passpct="{ row, column, value }">
      <PassRate v-tooltip="pctTooltip(row, column)" :value="value" :baseline-column="column" />
    </template>
    <template #failpct="{ row, column, value }">
      <PassRate v-tooltip="pctTooltip(row, column)" :value="value" :baseline-column="column" fail />
    </template>
    <template #downtime="{ row, column, value }">
      <span v-if="value" v-tooltip="(roundInt(row, column.id) / 60000).toFixed(0) + ' minutes'">
        {{ humanizeDuration(row, column.id) }}
      </span>
      <template v-else> - </template>
    </template>
    <template #percentage="{ row, column, value }">
      <span
        v-if="value !== null && !isNaN(value)"
        v-tooltip="pctTooltip(row, column)"
        :class="
          column.baseline != null
            ? `color-${baselineResult(
                roundPct(row, column.id),
                column.baseline,
                column.type,
                column.improvement,
              )}`
            : ``
        "
      >
        {{ roundPct(row, column.id) }}%
      </span>
      <template v-else> - </template>
    </template>
    <template #percentageDiff="{ row, column, value }">
      <span
        v-if="value !== null"
        v-tooltip="roundPct(row, column.id, 3, true) + ' percentage points'"
        :class="{
          'color-pass': column.positiveGood
            ? value > (column.threshold || 0)
            : value < (-column.threshold || 0),
          'color-fail': column.positiveGood
            ? value < (-column.threshold || 0)
            : value > (column.threshold || 0),
        }"
      >
        {{ roundPct(row, column.id, 2, true) }}pp
      </span>
      <template v-else> - </template>
    </template>
    <template #percentageChange="{ row, column, value }">
      <span
        v-if="value !== null"
        v-tooltip="roundPct(row, column.id, 1, true) + '%'"
        :class="{
          'color-pass': column.positiveGood
            ? value > (column.threshold || 0)
            : value < (-column.threshold || 0),
          'color-fail': column.positiveGood
            ? value < (-column.threshold || 0)
            : value > (column.threshold || 0),
        }"
      >
        {{ roundPct(row, column.id, 0, true) }}%
      </span>
      <template v-else> - </template>
    </template>
    <template #ms="{ row, column, value }">
      <span
        v-if="value !== null"
        v-tooltip="msTooltip(row, column)"
        :class="column.baseline ? `color-${baselineResult(value, column.baseline)}` : ``"
      >
        {{ roundInt(row, column.id) }} ms
      </span>
      <template v-else> - </template>
    </template>

    <template #msDiff="{ row, column, value }">
      <span
        v-if="value !== null"
        v-tooltip="roundFlt(row, column.id, 3, true) + ' milliseconds change'"
        :class="{
          'color-pass': value < (-column.threshold || 0),
          'color-fail': value > (column.threshold || 0),
        }"
      >
        {{ roundInt(row, column.id, true) }} ms
      </span>
      <template v-else> - </template>
    </template>

    <template #diff="{ row, column, value }">
      <span
        v-if="value !== null"
        v-tooltip="roundInt(row, column.id, true) + ' change'"
        :class="{
          'color-pass': value > (column.threshold || 0),
          'color-fail': value < (-column.threshold || 0),
        }"
      >
        {{ roundInt(row, column.id, true) }}
      </span>
      <template v-else> - </template>
    </template>

    <template #bool="{ value }">
      <span v-if="value === null"> - </span>
      <BtnSwitch v-else :value="value" xs disabled />
    </template>

    <template #tags="{ column, value }">
      <span
        v-if="value"
        v-tooltip="{
          content: value.join(', '),
          placement: 'top-start',
        }"
        @click.stop
      >
        <a
          v-for="(tag, idx) in value"
          :key="'tag-' + tag + '-' + idx"
          class="badge badge-info"
          href=""
          @click.prevent="setFilter(column.id, tag)"
          @keyup.enter="setFilter(column.id, tag)"
        >
          {{ tag }}
        </a>
      </span>
      <span v-else> - </span>
    </template>

    <template #pii="{ column, value }">
      <span
        v-if="value"
        v-tooltip="{
          content: value.detail.join(', '),
          placement: 'top-start',
        }"
        @click.stop
      >
        <a
          v-for="(tag, idx) in value.topLevel"
          :key="'tag-' + tag + '-' + idx"
          class="badge badge-info"
          href=""
          @click.prevent="setFilter(column.id, tag)"
          @keyup.enter="setFilter(column.id, tag)"
        >
          {{ tag }}
        </a>
      </span>
      <span v-else> - </span>
    </template>

    <template #badge="{ row, column, value }">
      <span
        v-if="value"
        v-tooltip="column.textFn ? column.textFn(row) : value"
        class="badge badge-info"
      >
        {{ column.textFn ? column.textFn(row) : value }}
      </span>
      <span v-else> - </span>
    </template>

    <template v-for="name in ['base_resource_id', 'resource_id']" #[name]="{ row, column, value }">
      <router-link
        v-if="column.linkTo && value"
        :key="'templ-' + name"
        v-tooltip="column.getterFn(value, row)"
        :to="column.linkTo(value, row)"
        @click.stop
      >
        {{ column.getterFn(value, row) || value }}
      </router-link>
      <a
        v-else-if="column.linkHref && value"
        :key="'templ-' + name"
        v-tooltip="column.getterFn(value, row)"
        :href="column.linkHref(value, row)"
        @click.stop
      >
        {{ column.getterFn(value, row) || value }}
      </a>
      <span v-else-if="value" :key="'templ-' + name" v-tooltip="column.getterFn(value, row)">
        {{ column.getterFn(value, row) || value }}
      </span>
      <span v-else :key="'templ-' + name">-</span>
    </template>

    <template #image="{ value }">
      <span
        v-if="value"
        v-tooltip="{
          content: imgTooltipContent(value),
          placement: 'top-start',
          html: true,
        }"
      >
        <img :src="value" alt="" style="height: 19px" />
      </span>
      <span v-else> - </span>
    </template>

    <template #datetime="{ column, value }">
      <LocalDateTime
        v-if="value"
        :datetime="value"
        :format="column.format"
        :show-tz="!column.hide_tz"
      />
      <span v-else> - </span>
    </template>

    <template #number="{ row, column, value }">
      <span v-if="value !== null" v-tooltip="number(row, column.id)">
        {{ number(row, column.id) }}
      </span>
      <template v-else> - </template>
    </template>
    <template #sparkline="{ value }">
      <SparkLine :data="value" />
    </template>

    <!-- pass through scoped slots -->
    <template v-for="(_, scopedSlotName) in $scopedSlots" #[scopedSlotName]="slotData">
      <slot :name="scopedSlotName" v-bind="slotData" />
    </template>

    <!-- pass through normal slots -->
    <template v-for="(_, slotName) in $slots" #[slotName]>
      <slot :name="slotName" />
    </template>

    <!-- override slots here -->
  </ResponsiveTable>
</template>

<script>
import _ from 'lodash';
import _get from 'lodash/get';
import _toUpper from 'lodash/toUpper';
import moment from 'moment-timezone';
import { call, get } from 'vuex-pathify';
import emojiFlags from 'emoji-flags';
import { CONTINENTS, CLOUDS } from '../store/agent-meta';
import LocalDateTime from './LocalDateTime.vue';
import PassRate from './PassRate.vue';
import ResponsiveTable from './ResponsiveTable.vue';
import SLOItem from './SLOItem.vue';
import BtnSwitch from './Switch.vue';
import SparkLine from './insights/SparkLine.vue';

export default {
  name: 'ResponsiveTableAdv',
  components: {
    BtnSwitch,
    LocalDateTime,
    PassRate,
    ResponsiveTable,
    SLOItem,
    SparkLine,
  },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    tableTitle: {
      type: String,
      required: false,
      default: '',
    },
    tableSubtitle: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    augmentedCols() {
      return this.columns.map((col) => {
        if (col.type === 'location') {
          return {
            ...col,
            textFn: (row) => this.locNameLong(this.valueFor(row, col.id)),
          };
        }
        if (col.type === 'continent') {
          return {
            ...col,
            textFn: (row) => this.getContinent(row.agent),
          };
        }
        if (col.type === 'target_continent') {
          return {
            ...col,
            textFn: (row) => this.getContinent(row.target),
          };
        }
        if (col.type === 'country') {
          return {
            ...col,
            textFn: (row) => this.getCountryName(row.agent),
          };
        }
        if (col.type === 'target_country') {
          return {
            ...col,
            textFn: (row) => this.getCountryName(row.target),
          };
        }
        if (col.type === 'target_address_mode') {
          return {
            ...col,
            textFn: (row) => this.getAddressMode(row.target),
          };
        }
        if (col.type === 'trigger') {
          return {
            ...col,
            textFn: (row) => this.getTriggerName(row, col.id),
          };
        }
        return col;
      });
    },
    locName: get('apimAPI/getLocNameShort'),
    locNameLong: get('apimAPI/getLocName'),
    isProjectEditor: get('account2/isProjectEditor'),
  },
  methods: {
    valueFor(row, id) {
      return _get(row, id, null);
    },
    baselineResult(val, baseline, category = 'ms', improvement = 'high') {
      if (baseline === null) {
        return '';
      }
      if (category === 'ms') {
        const value = Math.round(Math.abs(val), 0);
        const formattedBaseline = baseline / 1000;
        if (value < formattedBaseline) return 'pass';
        if (value > formattedBaseline * 1.05) return 'fail';
        return 'warn';
      }
      if (category === 'percentage') {
        const [v, b] =
          improvement === 'high' ? [val / 100, baseline] : [1 - val / 100, 1 - baseline];
        if (v >= b) return 'pass';
        if (v < b * 0.95) return 'fail';
        return 'warn';
      }
      if (category === 'casc') {
        if (val >= baseline) return 'pass';
        if (val < baseline * 0.95) return 'fail';
        return 'warn';
      }
      return ''; // If no criteria met, return empty string so no valid class is added
    },
    getContinent(location = null) {
      return location && location.continent ? CONTINENTS[location.continent] : '?';
    },
    getCloud(location = null) {
      return location && location.cloud
        ? CLOUDS[_toUpper(location.cloud).replaceAll(' ', '')]
        : CLOUDS.OTHER;
    },
    getCountry(location = null) {
      const info = location && location.country ? emojiFlags.countryCode(location.country) : null;
      if (info) {
        const { emoji, name } = info;
        return `${name} ${emoji}`;
      }
      return '?';
    },
    getCountryName(location = null) {
      const info = location && location.country ? emojiFlags.countryCode(location.country) : null;
      if (info) {
        const { name } = info;
        return name;
      }
      return '?';
    },
    getAddressMode(location = null) {
      if (location && location.address_mode) {
        return _.capitalize(location.address_mode);
      }
      return '?';
    },
    getTriggerName({ triggered = null }) {
      return triggered?.meta?.name || 'Manual';
    },
    roundPct(result, id, sigFig = 2, showSign = false) {
      const val = _get(result, id, 0) * 100;
      let sign = val > 0 ? '+' : '';
      sign = val < 0 ? '-' : sign;
      return `${showSign ? sign : ''}${Math.abs(val).toFixed(sigFig)}`;
    },
    roundCasc(result, id, showSign = false) {
      const val = _get(result, id, 0);
      const sign = val > 0 ? '+' : '';
      return `${showSign ? sign : ''}${(val / 100).toFixed(2)}`;
    },
    roundFlt(result, id, sigFig = 2, showSign = false) {
      const val = _get(result, id, 0);
      return `${showSign && val > 0 ? '+' : ''}${Math.abs(val).toFixed(sigFig)}`;
    },
    roundInt(result, id, showSign = false) {
      const val = _get(result, id, 0);
      let sign = val > 0 ? '+' : '';
      sign = val < 0 ? '-' : sign;
      const str = Math.round(Math.abs(val), 0);
      return `${showSign ? sign : ''}${str.toLocaleString()}`;
    },
    number(result, id) {
      const val = _get(result, id, 0);
      return Number(val).toLocaleString();
    },
    apdexTooltip(row, column) {
      let val = this.roundFlt(row, column.id, 3);
      if (column.prevId) {
        val += ` (was ${this.roundFlt(row, column.prevId, 3)})`;
      }
      return val;
    },
    cascTooltip(row, column) {
      let val = this.roundCasc(row, column.id);
      if (column.prevId) {
        val += ` (was ${this.roundCasc(row, column.prevId)})`;
      }
      return val;
    },
    msTooltip(row, column) {
      let val = `${this.roundFlt(row, column.id, 3)} milliseconds`;
      if (column.prevId) {
        val += ` (was ${this.roundFlt(row, column.prevId, 3)})`;
      }
      return val;
    },
    pctTooltip(row, column) {
      let val = `${this.roundPct(row, column.id, 3)}%`;
      if (column.prevId) {
        val += ` (was ${this.roundPct(row, column.prevId, 3)}%)`;
      }
      return val;
    },
    humanizeDuration(row, columnId) {
      const val = this.valueFor(row, columnId);
      return moment.duration(val).humanize(false);
    },
    imgTooltipContent(url) {
      return `
        <img
          src="${url}"
          alt="${url}"
          style="height:140px">
      `;
    },
    isNaN(val) {
      return Number.isNaN(val);
    },
    setFilter(colId, val) {
      this.$refs.theTable.setFilter(colId, val);
    },
    async goToProject(id) {
      await this.setProject({ id });
      this.$router.push({ name: 'project-home', query: { project_key_str: id } });
    },
    setProject: call('account2/setProject'),
  },
};
</script>
